import { ProjectDataTypes } from '@mono/proto-clients';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../../../contexts/authContext';
import { deliveryClient } from '../../../lib/connect/clients';
/**
 * OD-92 deep link (GRW-16 item 2a): `/capture/{id}?logTouchpoint=1&contact={contactId}`
 * opens Log touchpoint with that person already added. Registry Person pages
 * and chat link here; the contract is stable. Used by the new Home and by the
 * flag-off tabbed page.
 *
 * The person is resolved with the same query TouchpointParticipants uses for
 * linked people (`['touchpoint-people', tenantId, '']`), so the sheet opens
 * once that lookup settles. Without `canEdit` the params are removed silently
 * and nothing opens.
 */ export function useLogTouchpointDeepLink({ canEdit, ready = true }) {
    const [params, setParams] = useSearchParams();
    const requested = params.get('logTouchpoint') === '1';
    const contactId = params.get('contact') ?? '';
    // useAuth, not useTenantId: this also runs on the flag-off page, which must never throw on it.
    const tenantId = useAuth().user?.tenant_id ?? '';
    const people = useQuery({
        queryKey: [
            'touchpoint-people',
            tenantId,
            ''
        ],
        enabled: requested && canEdit && !!contactId && !!tenantId,
        staleTime: 60000,
        queryFn: async ({ signal })=>(await deliveryClient.listContacts({
                companyId: tenantId,
                search: ''
            }, {
                signal
            })).contacts
    });
    const clearParams = useCallback(()=>{
        setParams((current)=>{
            const next = new URLSearchParams(current);
            next.delete('logTouchpoint');
            next.delete('contact');
            return next;
        }, {
            replace: true
        });
    }, [
        setParams
    ]);
    // close() first unmounts the sheet, then drops the params in an effect: the
    // sheet's navigation blocker (dirty with the preselected person) is gone by
    // then, so the replace is never intercepted after "Discard changes".
    const [dismissed, setDismissed] = useState(false);
    const close = useCallback(()=>setDismissed(true), []);
    useEffect(()=>{
        if (!dismissed) return;
        if (requested) clearParams();
        else setDismissed(false);
    }, [
        dismissed,
        requested,
        clearParams
    ]);
    // A reader following the link sees the pursuit, with no sheet and no lingering params.
    // `ready` keeps this from firing while the capability is still resolving.
    useEffect(()=>{
        if (ready && requested && !canEdit) clearParams();
    }, [
        ready,
        requested,
        canEdit,
        clearParams
    ]);
    const person = contactId ? people.data?.find((p)=>p.id === contactId) : undefined;
    // A cached list (60 s, shared with the attendee picker) can predate the
    // contact: refetch once before deciding the person is unknown.
    const needsFresh = !!contactId && !!people.data && !person && !people.isFetchedAfterMount && !people.isError;
    const { refetch, isFetching } = people;
    useEffect(()=>{
        if (needsFresh && !isFetching) void refetch();
    }, [
        needsFresh,
        isFetching,
        refetch
    ]);
    const initialParticipants = useMemo(()=>{
        if (!person) return [];
        return [
            new ProjectDataTypes.TouchpointParticipant({
                id: crypto.randomUUID(),
                contactId: person.id,
                attendeeName: `${person.firstName} ${person.lastName}`.trim(),
                organization: person.governmentOrganizationName || person.companyProfileName || person.organization
            })
        ];
    }, [
        person
    ]);
    // The sheet copies its attendees once, on mount: open only after the person is resolved
    // (found, or a fresh read without them, or a failed read).
    const settled = !contactId || !!person || people.isError || !needsFresh && !people.isFetching && !people.isLoading;
    return {
        open: ready && requested && canEdit && settled && !dismissed,
        initialParticipants,
        close
    };
}
